/**
 * Message Persistence Merge
 *
 * Merges persisted chat rows back into optimistic chat state.
 *
 * Primary responsibilities:
 * - Prefer persisted database rows over optimistic temp rows
 * - Preserve current-only messages that persistence has not returned
 * - Keep streamed Thought / tool annotation sections across finalize
 * - Deduplicate twin assistant rows (same id or same chat+sequence+role)
 *
 * Dependencies:
 * - types/chat-message
 * - mergeAssistantMessageAnnotation
 */

import { mergeAssistantMessageAnnotation } from '@/lib/chat/merge-assistant-message-annotation'
import type { Json, Tables } from '@/supabase/types'
import type { ChatMessage } from '@/types'

type MessagePersistenceIdentity = Pick<
  Tables<'messages'>,
  'id' | 'sequence_number' | 'role'
>

export function getMessagePersistenceSlot(
  message: MessagePersistenceIdentity
): string {
  return `${message.sequence_number}:${message.role}`
}

export function getChatMessageIdentitySlot(chatMessage: ChatMessage): string {
  const chatId = chatMessage.message.chat_id || 'chat'
  return `${chatId}:${getMessagePersistenceSlot(chatMessage.message)}`
}

function getContentLength(chatMessage: ChatMessage): number {
  const content = chatMessage.message.content
  return typeof content === 'string' ? content.trim().length : 0
}

function getUpdatedAtTime(chatMessage: ChatMessage): number {
  const updatedAt = chatMessage.message.updated_at
  if (!updatedAt) return 0
  const parsed = Date.parse(updatedAt)
  return Number.isFinite(parsed) ? parsed : 0
}

/**
 * Prefer the richer / newer row when twins collide (same id, or same
 * chat+sequence+role). Prevents two Message components after finalize/refetch.
 */
export function preferRicherChatMessage(
  left: ChatMessage,
  right: ChatMessage
): ChatMessage {
  const leftLen = getContentLength(left)
  const rightLen = getContentLength(right)
  if (leftLen !== rightLen) {
    return rightLen > leftLen ? right : left
  }

  const leftUpdated = getUpdatedAtTime(left)
  const rightUpdated = getUpdatedAtTime(right)
  if (leftUpdated !== rightUpdated) {
    return rightUpdated > leftUpdated ? right : left
  }

  // Stable tie-break on id so repeated merges don't thrash.
  return right.message.id > left.message.id ? right : left
}

/**
 * Collapse consecutive assistant bubbles where one content is a prefix of the
 * other (stream finalize twin: full reply + truncated copy with different
 * sequence numbers). Keep the richer row.
 */
export function collapsePrefixTwinAssistants(
  messages: ChatMessage[]
): ChatMessage[] {
  const sorted = [...messages].sort(
    (a, b) => a.message.sequence_number - b.message.sequence_number
  )
  const out: ChatMessage[] = []
  for (const msg of sorted) {
    const prev = out[out.length - 1]
    if (
      prev &&
      prev.message.role === 'assistant' &&
      msg.message.role === 'assistant' &&
      (prev.message.chat_id || '') === (msg.message.chat_id || '')
    ) {
      const a =
        typeof prev.message.content === 'string'
          ? prev.message.content.trim()
          : ''
      const b =
        typeof msg.message.content === 'string'
          ? msg.message.content.trim()
          : ''
      if (a && b && (a === b || a.startsWith(b) || b.startsWith(a))) {
        out[out.length - 1] = preferRicherChatMessage(prev, msg)
        continue
      }
    }
    out.push(msg)
  }
  return out
}

export function dedupeChatMessagesByIdentity(
  messages: ChatMessage[]
): ChatMessage[] {
  const byId = new Map<string, ChatMessage>()
  const withoutIds: ChatMessage[] = []

  for (const chatMessage of messages) {
    const id = chatMessage.message.id?.trim()
    if (!id) {
      withoutIds.push(chatMessage)
      continue
    }
    const existing = byId.get(id)
    byId.set(
      id,
      existing ? preferRicherChatMessage(existing, chatMessage) : chatMessage
    )
  }

  const bySlot = new Map<string, ChatMessage>()
  for (const chatMessage of [...byId.values(), ...withoutIds]) {
    const slot = getChatMessageIdentitySlot(chatMessage)
    const existing = bySlot.get(slot)
    bySlot.set(
      slot,
      existing ? preferRicherChatMessage(existing, chatMessage) : chatMessage
    )
  }

  return collapsePrefixTwinAssistants(
    [...bySlot.values()].sort(
      (a, b) => a.message.sequence_number - b.message.sequence_number
    )
  )
}

export function filterUnpersistedChatMessages(
  currentMessages: ChatMessage[],
  persistedMessages: MessagePersistenceIdentity[]
): ChatMessage[] {
  const persistedMessageIds = new Set(
    persistedMessages
      .map((message) => message.id)
      .filter((id): id is string => Boolean(id))
  )
  const persistedMessageSlots = new Set(
    persistedMessages.map(getMessagePersistenceSlot)
  )

  return currentMessages.filter((chatMessage) => {
    const messageId = chatMessage.message.id

    if (messageId && persistedMessageIds.has(messageId)) {
      return false
    }

    return !persistedMessageSlots.has(
      getMessagePersistenceSlot(chatMessage.message)
    )
  })
}

export function mergePersistedChatMessages(
  persistedMessages: ChatMessage[],
  currentMessages: ChatMessage[]
): ChatMessage[] {
  const dedupedPersisted = dedupeChatMessagesByIdentity(persistedMessages)
  const currentOnlyMessages = filterUnpersistedChatMessages(
    currentMessages,
    dedupedPersisted.map((chatMessage) => chatMessage.message)
  )

  // Carry the optimistic message's stable render identity onto the persisted
  // message that replaces it (same slot), so the React key does not change when
  // the temp id becomes the real DB id. This is what prevents the finalize
  // remount that crashed new chats and moved the thought to the bottom.
  const renderKeyBySlot = new Map<string, string>()
  const liveAnnotationBySlot = new Map<string, Json | null>()
  for (const chatMessage of currentMessages) {
    const slot = getMessagePersistenceSlot(chatMessage.message)
    const existingKey =
      chatMessage.renderKey?.trim() || chatMessage.message.id?.trim() || ''
    if (existingKey && !renderKeyBySlot.has(slot)) {
      renderKeyBySlot.set(slot, existingKey)
    }
    if (!liveAnnotationBySlot.has(slot)) {
      liveAnnotationBySlot.set(
        slot,
        (chatMessage.message.annotation as Json | null | undefined) ?? null
      )
    }
  }

  const persistedWithStableKeys = dedupedPersisted.map((chatMessage) => {
    const slot = getMessagePersistenceSlot(chatMessage.message)
    const stableKey =
      chatMessage.renderKey?.trim() ||
      renderKeyBySlot.get(slot) ||
      chatMessage.message.id?.trim()

    // Finalize used to drop the live streaming annotation when the persisted
    // row omitted client-only Thought/tool sections. Merge so the completed
    // message keeps what the user already saw while streaming.
    const mergedAnnotation = mergeAssistantMessageAnnotation(
      liveAnnotationBySlot.get(slot),
      (chatMessage.message.annotation as Json | null | undefined) ?? null
    )

    const nextMessage: ChatMessage =
      mergedAnnotation === chatMessage.message.annotation
        ? chatMessage
        : {
            ...chatMessage,
            message: {
              ...chatMessage.message,
              annotation: mergedAnnotation
            }
          }

    return stableKey ? { ...nextMessage, renderKey: stableKey } : nextMessage
  })

  return dedupeChatMessagesByIdentity([
    ...persistedWithStableKeys,
    ...currentOnlyMessages
  ])
}
